import React, { useState, useEffect, useRef } from 'react';
import {
  User, Settings, Building2, CreditCard, Bell, Mail, Phone,
  Calendar, Clock, CheckCircle, AlertCircle, XCircle, Upload, Eye, Trash2,
  Edit2, Save, ChevronDown, ChevronUp, ArrowLeft, Shield,
  FileText, AlertTriangle, RefreshCw, Users, MapPin
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { db, storage, userSubmissions, paymentProofs, notificationPreferences, supabase } from '../lib/supabase';

// ==================== CONSTANTS ====================
const PAYMENT_METHODS = [
  { id: 'bank_transfer', label: 'Bank Transfer' },
  { id: 'ncb', label: 'NCB (National Commercial Bank)' },
  { id: 'scotiabank', label: 'Scotiabank' },
  { id: 'sagicor', label: 'Sagicor Bank' },
  { id: 'jn_bank', label: 'JN Bank' },
  { id: 'cash', label: 'Cash Deposit' },
  { id: 'other', label: 'Other' }
];

const TIER_PRICES = {
  free: { monthly: 0, yearly: 0 },
  basic: { monthly: 3500, yearly: 37800 },
  premium: { monthly: 5000, yearly: 54000 }
};

const BUSINESS_TYPE_ICONS = {
  tutor: Users,
  math_tutor: Users,
  science_tutor: Users,
  language_tutor: Users,
  test_prep: Building2,
  consultant: Users,
  school_supplies: Building2,
  uniforms: Building2,
  private_school: Building2,
  other: Building2
};

// ==================== STATUS BADGE ====================
const StatusBadge = ({ status, type = 'submission' }) => {
  const configs = {
    submission: {
      pending: { color: 'amber', icon: Clock, label: 'Pending Review' },
      approved: { color: 'emerald', icon: CheckCircle, label: 'Approved' },
      rejected: { color: 'red', icon: XCircle, label: 'Rejected' }
    },
    payment: {
      pending: { color: 'amber', icon: Clock, label: 'Pending Verification' },
      received: { color: 'blue', icon: Eye, label: 'Payment Received' },
      verified: { color: 'emerald', icon: CheckCircle, label: 'Verified' },
      rejected: { color: 'red', icon: XCircle, label: 'Rejected' },
      refunded: { color: 'gray', icon: RefreshCw, label: 'Refunded' }
    }
  };

  const config = configs[type][status] || configs[type].pending;
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-${config.color}-100 text-${config.color}-700`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </span>
  );
};

// ==================== DASHBOARD SECTION ====================
const DashboardSection = ({ title, icon: Icon, children, defaultOpen = true, badge = null }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-4 flex items-center justify-between bg-gradient-to-r from-gray-50 to-white hover:from-gray-100 transition-colors"
      >
        <div className="flex items-center gap-3">
          <Icon className="w-5 h-5 text-brand-600" />
          <h3 className="font-bold text-lg">{title}</h3>
          {badge && (
            <span className="px-2 py-0.5 bg-brand-100 text-brand-700 text-xs font-medium rounded-full">
              {badge}
            </span>
          )}
        </div>
        {isOpen ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
      </button>
      {isOpen && <div className="px-6 pb-6">{children}</div>}
    </div>
  );
};

// ==================== TOGGLE SWITCH COMPONENT ====================
const ToggleSwitch = ({ enabled, onToggle, disabled = false }) => {
  return (
    <button
      onClick={onToggle}
      disabled={disabled}
      className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
        enabled ? 'bg-brand-500' : 'bg-gray-300'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform ${
          enabled ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );
};

// ==================== ACCOUNT SETTINGS SECTION ====================
const AccountSettings = ({ profile, onUpdate, onSwitchToBusinessAccount, onDeleteAccount }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    full_name: profile?.full_name || '',
    phone: profile?.phone || '',
    parish: profile?.parish || ''
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await onUpdate(form);
    setSaving(false);
    setEditing(false);
  };

  return (
    <div className="space-y-4 pt-4">
      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-600 mb-1">Full Name</label>
          {editing ? (
            <input
              type="text"
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
          ) : (
            <p className="px-4 py-2 bg-gray-50 rounded-lg">{profile?.full_name || '-'}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-600 mb-1">Email</label>
          <p className="px-4 py-2 bg-gray-100 rounded-lg text-gray-500">{profile?.email}</p>
          <p className="text-xs text-gray-400 mt-1">Email cannot be changed</p>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-600 mb-1">Phone</label>
          {editing ? (
            <input
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
              placeholder="876-XXX-XXXX"
            />
          ) : (
            <p className="px-4 py-2 bg-gray-50 rounded-lg">{profile?.phone || '-'}</p>
          )}
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-600 mb-1">Parish</label>
          {editing ? (
            <select
              value={form.parish}
              onChange={(e) => setForm({ ...form, parish: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            >
              <option value="">Select Parish</option>
              {['Kingston', 'St. Andrew', 'St. Thomas', 'Portland', 'St. Mary', 'St. Ann', 'Trelawny', 'St. James', 'Hanover', 'Westmoreland', 'St. Elizabeth', 'Manchester', 'Clarendon', 'St. Catherine'].map(p => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          ) : (
            <p className="px-4 py-2 bg-gray-50 rounded-lg">{profile?.parish || '-'}</p>
          )}
        </div>
      </div>
      <div className="flex items-center justify-between pt-2">
        <div className="flex gap-2">
          {editing ? (
            <>
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white font-medium rounded-lg flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                onClick={() => setEditing(false)}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 font-medium rounded-lg"
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              onClick={() => setEditing(true)}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg flex items-center gap-2"
            >
              <Edit2 className="w-4 h-4" />
              Edit Profile
            </button>
          )}
        </div>
        <button
          onClick={onDeleteAccount}
          className="text-sm text-gray-400 hover:text-red-600 flex items-center gap-1 transition-colors"
        >
          <Trash2 className="w-3 h-3" />
          Delete Account
        </button>
      </div>
      <div className="mt-4 p-4 bg-gray-50 rounded-xl">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <User className="w-4 h-4" />
          <span>Account Type: <strong className="capitalize">{profile?.user_type === 'service_provider' ? 'Education Provider' : 'Parent/Student'}</strong></span>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-500 mt-2">
          <Calendar className="w-4 h-4" />
          <span>Member since: {new Date(profile?.created_at).toLocaleDateString('en-JM', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
        </div>
      </div>

      {/* Switch to Business Account */}
      {profile?.user_type !== 'service_provider' && (
        <div className="mt-4 p-4 bg-violet-50 border border-violet-200 rounded-xl">
          <div className="flex items-start gap-3">
            <Building2 className="w-5 h-5 text-violet-600 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-medium text-violet-800">Want to list your business?</h4>
              <p className="text-sm text-violet-600 mt-1">
                Switch to a business account to list your education services and reach thousands of Jamaican families.
              </p>
              <button
                onClick={onSwitchToBusinessAccount}
                className="mt-3 px-4 py-2 bg-violet-600 hover:bg-violet-700 text-white font-medium rounded-lg text-sm"
              >
                Switch to Business Account
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

// ==================== BUSINESS LISTINGS SECTION ====================
const BusinessListings = ({ listings, onUploadPayment, setPage }) => {
  if (listings.length === 0) {
    return (
      <div className="pt-4">
        <div className="text-center py-8 bg-gray-50 rounded-xl">
          <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 mb-4">You haven't submitted any business listings yet.</p>
          <button
            onClick={() => setPage('getlisted')}
            className="px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white font-medium rounded-lg"
          >
            Get Listed
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 pt-4">
      {listings.map((listing) => {
        const TypeIcon = BUSINESS_TYPE_ICONS[listing.business_type] || Building2;
        const tierPrice = TIER_PRICES[listing.tier] || TIER_PRICES.free;
        const needsPayment = listing.tier !== 'free' && listing.payment_status === 'pending';

        return (
          <div key={listing.id} className="border rounded-xl p-4 hover:border-brand-300 transition-colors">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                {listing.logo_url ? (
                  <img src={listing.logo_url} alt="" className="w-12 h-12 rounded-lg object-cover" />
                ) : (
                  <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                    <TypeIcon className="w-6 h-6 text-gray-400" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <h4 className="font-bold text-lg truncate">{listing.business_name}</h4>
                  <p className="text-sm text-gray-500 capitalize truncate">{listing.business_type?.replace('_', ' ')}</p>
                </div>
              </div>
              <div className="text-right">
                <div className="flex items-center gap-2 justify-end mb-1">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                    listing.tier === 'premium' ? 'bg-amber-100 text-amber-700' :
                    listing.tier === 'basic' ? 'bg-blue-100 text-blue-700' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {listing.tier?.charAt(0).toUpperCase() + listing.tier?.slice(1)} Tier
                  </span>
                </div>
                <StatusBadge status={listing.status} type="submission" />
              </div>
            </div>

            {/* Listing Details */}
            <div className="mt-4 grid md:grid-cols-3 gap-4 text-sm">
              <div className="flex items-center gap-2 text-gray-600">
                <MapPin className="w-4 h-4 text-gray-400" />
                {listing.parish || '-'}
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Mail className="w-4 h-4 text-gray-400" />
                {listing.contact_email}
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Phone className="w-4 h-4 text-gray-400" />
                {listing.contact_phone || '-'}
              </div>
            </div>

            {/* Dates and Payment Status */}
            {listing.status === 'approved' && (
              <div className="mt-4 p-3 bg-brand-50 rounded-lg">
                <div className="grid md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Activation Date:</span>
                    <p className="font-medium">{listing.activation_date ? new Date(listing.activation_date).toLocaleDateString('en-JM') : 'Pending payment'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Expiration Date:</span>
                    <p className="font-medium">{listing.expiration_date ? new Date(listing.expiration_date).toLocaleDateString('en-JM') : '-'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Payment Status:</span>
                    <div className="mt-1">
                      <StatusBadge status={listing.payment_status} type="payment" />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Rejection Reason */}
            {listing.status === 'rejected' && listing.rejection_reason && (
              <div className="mt-4 p-3 bg-red-50 rounded-lg">
                <div className="flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-red-500 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-red-700">Rejection Reason:</p>
                    <p className="text-sm text-red-600">{listing.rejection_reason}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Payment Action */}
            {needsPayment && listing.status !== 'rejected' && (
              <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div className="flex-1">
                    <h5 className="font-medium text-amber-800">Payment Required</h5>
                    <p className="text-sm text-amber-700 mt-1">
                      To activate your {listing.tier} listing, please submit proof of payment for J${tierPrice.yearly.toLocaleString()}/year.
                    </p>
                    <button
                      onClick={() => onUploadPayment(listing)}
                      className="mt-3 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white font-medium rounded-lg flex items-center gap-2"
                    >
                      <Upload className="w-4 h-4" />
                      Upload Proof of Payment
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Submitted Date */}
            <div className="mt-4 pt-3 border-t text-xs text-gray-400">
              Submitted: {new Date(listing.created_at).toLocaleDateString('en-JM', { year: 'numeric', month: 'long', day: 'numeric' })}
            </div>
          </div>
        );
      })}

      <div className="text-center pt-4">
        <button
          onClick={() => setPage('getlisted')}
          className="px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white font-medium rounded-lg"
        >
          Submit Another Listing
        </button>
      </div>
    </div>
  );
};

// ==================== PAYMENT UPLOAD MODAL ====================
const PaymentUploadModal = ({ listing, onClose, onSubmit }) => {
  const [form, setForm] = useState({
    payment_method: '',
    payment_reference: '',
    payment_date: '',
    amount: TIER_PRICES[listing?.tier]?.yearly || 0,
    notes: ''
  });
  const [proofFile, setProofFile] = useState(null);
  const [proofPreview, setProofPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
      setError('Please select an image or PDF file');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError('File must be less than 10MB');
      return;
    }

    setProofFile(file);
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (event) => setProofPreview(event.target.result);
      reader.readAsDataURL(file);
    } else {
      setProofPreview(null);
    }
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!proofFile) {
      setError('Please upload proof of payment');
      return;
    }

    if (!form.payment_method) {
      setError('Please select a payment method');
      return;
    }

    setUploading(true);

    try {
      // Upload proof file
      const ext = proofFile.name.split('.').pop();
      const path = `proofs/${listing.id}_${Date.now()}.${ext}`;
      const { error: uploadError } = await storage.uploadImage('payment-proofs', path, proofFile);

      if (uploadError) throw uploadError;

      const proofUrl = storage.getPublicUrl('payment-proofs', path);

      await onSubmit({
        submission_id: listing.id,
        proof_url: proofUrl,
        payment_method: form.payment_method,
        payment_reference: form.payment_reference,
        payment_date: form.payment_date || null,
        amount: form.amount,
        notes: form.notes
      });

      onClose();
    } catch (err) {
      setError('Error uploading: ' + (err.message || 'Please try again'));
    } finally {
      setUploading(false);
    }
  };

  if (!listing) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b sticky top-0 bg-white">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-xl">Upload Proof of Payment</h3>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <XCircle className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          <p className="text-sm text-gray-500 mt-1">For: {listing.business_name}</p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-4 h-4" />
              {error}
            </div>
          )}

          {/* Payment Info */}
          <div className="p-4 bg-brand-50 rounded-xl">
            <h4 className="font-medium text-brand-800 mb-2">Payment Details</h4>
            <p className="text-sm text-brand-700">
              We'll email your banking details and payment instructions once your submission has been reviewed. Once you've paid, upload your proof below.<br />
              <strong>Amount:</strong> J${TIER_PRICES[listing.tier]?.yearly.toLocaleString()}/year
            </p>
          </div>

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Proof of Payment (Receipt/Screenshot) *
            </label>
            <div
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center cursor-pointer hover:border-brand-500 transition-colors"
            >
              {proofPreview ? (
                <img src={proofPreview} alt="Preview" className="max-h-40 max-w-full mx-auto rounded-lg object-contain" />
              ) : proofFile ? (
                <div className="flex items-center justify-center gap-2">
                  <FileText className="w-8 h-8 text-gray-400" />
                  <span className="text-sm text-gray-600">{proofFile.name}</span>
                </div>
              ) : (
                <>
                  <Upload className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">Click to upload receipt or screenshot</p>
                  <p className="text-xs text-gray-400 mt-1">PNG, JPG, or PDF (max 10MB)</p>
                </>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.pdf"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>

          {/* Payment Method */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method *</label>
            <select
              value={form.payment_method}
              onChange={(e) => setForm({ ...form, payment_method: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500"
              required
            >
              <option value="">Select method</option>
              {PAYMENT_METHODS.map(m => (
                <option key={m.id} value={m.id}>{m.label}</option>
              ))}
            </select>
          </div>

          {/* Reference Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Reference/Confirmation Number</label>
            <input
              type="text"
              value={form.payment_reference}
              onChange={(e) => setForm({ ...form, payment_reference: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500"
              placeholder="Bank confirmation or receipt number"
            />
          </div>

          {/* Payment Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Payment Date</label>
            <input
              type="date"
              value={form.payment_date}
              onChange={(e) => setForm({ ...form, payment_date: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Additional Notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500"
              rows={2}
              placeholder="Any additional information..."
            />
          </div>

          {/* Submit Button */}
          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={uploading}
              className="flex-1 py-3 bg-brand-600 hover:bg-brand-700 text-white font-bold rounded-xl flex items-center justify-center gap-2"
            >
              {uploading ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4" />
                  Submit Payment Proof
                </>
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold rounded-xl"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ==================== NOTIFICATION PREFERENCES SECTION ====================
const NotificationPreferencesSection = ({ preferences, onUpdate, isBusinessAccount }) => {
  const [prefs, setPrefs] = useState(preferences || {
    email_listing_updates: true,
    email_payment_reminders: true,
    email_expiry_warnings: true,
    email_promotional: false,
    email_newsletter: false
  });
  const [saving, setSaving] = useState(false);

  const handleToggle = async (key) => {
    const newPrefs = { ...prefs, [key]: !prefs[key] };
    setPrefs(newPrefs);
    setSaving(true);
    await onUpdate(newPrefs);
    setSaving(false);
  };

  // Filter preferences based on account type
  const allPrefItems = [
    { key: 'email_listing_updates', label: 'Listing Status Updates', desc: 'Get notified when your listing is approved or needs attention', businessOnly: true },
    { key: 'email_payment_reminders', label: 'Listing Payment Reminders', desc: 'Receive reminders about pending listing payments', businessOnly: true },
    { key: 'email_expiry_warnings', label: 'Listing Expiration Warnings', desc: 'Get notified before your listing expires', businessOnly: true },
    { key: 'email_promotional', label: 'Promotional Emails', desc: 'Special offers and discounts', businessOnly: false },
    { key: 'email_newsletter', label: 'Newsletter', desc: 'Monthly updates about new features and tips', businessOnly: false }
  ];

  const prefItems = allPrefItems.filter(item => !item.businessOnly || isBusinessAccount);

  return (
    <div className="space-y-4 pt-4">
      {prefItems.map((item) => (
        <div key={item.key} className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
          <div>
            <h4 className="font-medium">{item.label}</h4>
            <p className="text-sm text-gray-500">{item.desc}</p>
          </div>
          <ToggleSwitch
            enabled={prefs[item.key]}
            onToggle={() => handleToggle(item.key)}
            disabled={saving}
          />
        </div>
      ))}
    </div>
  );
};

// ==================== MAIN USER DASHBOARD ====================
export const UserDashboard = ({ setPage, goBack }) => {
  const { user, profile, updateProfile, deleteAccount } = useAuth();
  const [listings, setListings] = useState([]);
  const [notifPrefs, setNotifPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentModalListing, setPaymentModalListing] = useState(null);

  useEffect(() => {
    if (user) {
      // Load dashboard data even if profile is null (will show empty state)
      loadDashboardData();
    } else {
      setLoading(false);
    }
  }, [user]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      // Load user's business listings (by user_id or email)
      let listingsData = [];
      const { data: byUser } = await userSubmissions.getByUser(user.id);
      if (byUser && byUser.length > 0) {
        listingsData = byUser;
      } else if (profile?.email) {
        // Fallback to email lookup for existing submissions
        const { data: byEmail } = await userSubmissions.getByEmail(profile.email);
        listingsData = byEmail || [];
        // Link them to user for future lookups
        for (const listing of listingsData) {
          if (!listing.user_id) {
            await userSubmissions.linkToUser(listing.id, user.id);
          }
        }
      }
      setListings(listingsData);

      // Load notification preferences
      const { data: prefsData } = await notificationPreferences.get(user.id);
      setNotifPrefs(prefsData);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async (updates) => {
    await updateProfile(updates);
  };

  const handlePaymentSubmit = async (paymentData) => {
    await paymentProofs.create({
      ...paymentData,
      user_id: user.id
    });
    // Update submission payment status
    await db.submissions.update?.(paymentData.submission_id, { payment_status: 'received' });
    // Reload listings
    loadDashboardData();
  };

  const handleUpdateNotifPrefs = async (prefs) => {
    await notificationPreferences.update(user.id, prefs);
    setNotifPrefs(prefs);
  };

  const handleSwitchToBusinessAccount = async () => {
    if (window.confirm('Switch to a business account? This will allow you to submit business listings.')) {
      await updateProfile({ user_type: 'service_provider' });
    }
  };

  const handleDeleteAccount = async () => {
    if (window.confirm('Are you sure you want to delete your account? This action cannot be undone and all your data will be permanently removed.')) {
      if (window.confirm('This is your final warning. Delete account permanently?')) {
        await deleteAccount();
      }
    }
  };

  const isBusinessAccount = profile?.user_type === 'service_provider';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-8 h-8 text-brand-600 animate-spin" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="space-y-6">
        <button
          onClick={() => goBack ? goBack() : setPage('home')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium"
        >
          <ArrowLeft className="w-5 h-5" />Back
        </button>
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 text-center">
          <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h3 className="text-lg font-bold text-amber-800 mb-2">Profile Not Found</h3>
          <p className="text-amber-700 mb-4">
            Your profile hasn't been set up yet. Please try signing out and signing back in, or contact support if the issue persists.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white font-medium rounded-lg"
          >
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back Button */}
      <button
        onClick={() => goBack ? goBack() : setPage('home')}
        className="flex items-center gap-2 text-gray-600 hover:text-gray-900 font-medium"
      >
        <ArrowLeft className="w-5 h-5" />Back
      </button>

      {/* Header */}
      <div className="bg-gradient-to-r from-brand-700 to-brand-800 text-white rounded-2xl p-6 shadow-xl">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
            <User className="w-8 h-8" />
          </div>
          <div>
            <h2 className="text-2xl font-black">My Dashboard</h2>
            <p className="text-brand-200">Welcome back, {profile?.full_name?.split(' ')[0] || 'User'}</p>
          </div>
        </div>
      </div>

      {/* Quick Stats - Business Accounts Only */}
      {isBusinessAccount && (
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white rounded-xl p-4 shadow-md">
            <Building2 className="w-8 h-8 text-violet-500 mb-2" />
            <p className="text-2xl font-bold">{listings.length}</p>
            <p className="text-sm text-gray-500">Listings</p>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-md">
            <CheckCircle className="w-8 h-8 text-brand-500 mb-2" />
            <p className="text-2xl font-bold">{listings.filter(l => l.status === 'approved').length}</p>
            <p className="text-sm text-gray-500">Active</p>
          </div>
        </div>
      )}

      {/* Dashboard Sections */}
      <DashboardSection title="Account Settings" icon={Settings} defaultOpen={true}>
        <AccountSettings
          profile={profile}
          onUpdate={handleUpdateProfile}
          onSwitchToBusinessAccount={handleSwitchToBusinessAccount}
          onDeleteAccount={handleDeleteAccount}
        />
      </DashboardSection>

      {/* Business Listings - Only for business accounts */}
      {isBusinessAccount && (
        <DashboardSection
          title="Business Listings"
          icon={Building2}
          defaultOpen={true}
          badge={listings.length > 0 ? listings.length : null}
        >
          <BusinessListings
            listings={listings}
            onUploadPayment={setPaymentModalListing}
            setPage={setPage}
          />
        </DashboardSection>
      )}

      <DashboardSection title="Email Notifications" icon={Bell} defaultOpen={false}>
        <NotificationPreferencesSection
          preferences={notifPrefs}
          onUpdate={handleUpdateNotifPrefs}
          isBusinessAccount={isBusinessAccount}
        />
      </DashboardSection>

      {/* Payment Upload Modal */}
      {paymentModalListing && (
        <PaymentUploadModal
          listing={paymentModalListing}
          onClose={() => setPaymentModalListing(null)}
          onSubmit={handlePaymentSubmit}
        />
      )}
    </div>
  );
};

export default UserDashboard;
